import { lazy } from 'react';

import { NotFound } from 'pages/not-found/not-found';
import { Unauthorized } from 'pages/unauthorized/unauthorized';
import { ErrorPage } from 'components/error-handler/error-page';
import { GlobalSearchOpenAI } from 'pages/global-search/global-search-open-ai';
import { ConsoleAudit } from '../pages/console-audit/console-audit';
import { SastScan } from '../pages/sast-scan/sast-scan';
import { IacScan } from '../pages/iac-scan/iac-scan';
import { TrivyScan } from 'pages/trivy-scan/trivy-scan';
import { TrivyScanDetails } from 'pages/trivy-scan/trivy-scan-details/trivy-scan-details';
import { PRSecrets } from 'pages/pr-secrets/pr-secrets';
import { PRSecretsDetails } from 'pages/pr-secrets/pr-secrets-details/pr-secrets-details';
import { DastScan } from 'pages/dast-scan/dast-scan';

export const paths = {
  dashboard: '/',
  alerts: '/alerts',
  policies: '/policies',
  runbooks: '/runbooks',
  addRunbook: '/add-runbook',
  editRunbook: '/edit-runbook',
  editPolicy: '/policy',
  policyCriteria: '/policy-criteria',
  tenantSettings: '/tenant-settings',
  generateToken: '/generate-token',
  tenantSettingsWatchHide: '/tenant-settings-watch-hide',
  entityGraph: '/entity-graph',
  profileBilling: '/profile-billing',
  connectors: '/connectors',
  connectorsDetails: `/connectors/:id`,
  feedbackSupport: '/feedback-support',
  documentation: '/documentation',
  packageDependencyTree: '/dependency-tree',
  packageDependencyTreeById: '/dependency-tree/:id',
  opensourceDependencies: '/opensource-dependencies',
  opensourceDependencyDetails: '/opensource-dependencies/*',
  reporting: '/reporting',
  reports: '/reports',
  teamVulnerabilityReport: '/reports/team-vulnerability-report',
  orgVulnerabilityReport: '/reports/org-vulnerability-report',
  orgRiskReport: '/reports/org-risk-report',
  labeledTeamReport: '/reports/labeled-team-report',
  resources: '/resources',
  secrets: '/secrets',
  sast: '/sast',
  dast: '/dast',
  iac: '/iac',
  trivy: '/github/terraform',
  trivyDetails: '/github/terraform/:org/:repo/:pr_number/:check_run_id',
  prSecrets: '/github/secrets',
  prSecretsDetails: '/github/secrets/:org/:repo/:pr_number/:check_run_id',
  identities: '/identities',
  commitDetails: '/commit-details/:repoSlug/:sha',
  commitDetailsRootRoute: '/commit-details',
  identityDetails: '/identity',
  assets: '/assets',
  assetsDetails: '/assets-details',
  artifactoryDetails: '/artifactory-details',
  cloudAssetsDetails: '/cloud-assets-details',
  cloudAccountDetails: '/cloud-account-details',
  teams: '/teams',
  teamsDetails: '/team',
  orgTeamsDetails: '/org-team',
  console: '/console-audit',
  branches: '/branches',
  userManagement: '/user-management',
  repositories: '/repositories',
  artifactory: '/artifactory',
  cloudAssets: '/cloud-assets',
  cloudAccounts: '/cloud-accounts',
  cloudAlerts: '/cloud-alerts',
  containerAlerts: '/container-alerts',
  registry: '/registry',
  recommendationsStatus: '/recommendations-status',
  recommendationsOverview: '/recommendations',
  recommendationsOverviewIdentity: '/recommendations-identity',
  recommendationsOverviewAsset: '/recommendations-asset',
  recommendationsOverviewTeam: '/recommendations-team',
  recommendationDetails: '/recommendations/:recommendationId',
  recommendationUserDetails: '/recommendations/users/:id',
  recommendationTeamDetails: '/recommendations/teams/:id',
  recommendationTeamMemberDetails: '/recommendations/:recommendationType/:id',
  complianceOverview: '/compliance-overview',
  complianceDetails: '/compliance-overview/:complianceId',
  compliancePolicyDetails:
    '/compliance-overview/:complianceId/:compliancePolicyId',
  createCompliance: '/create-compliance',
  editCompliance: '/edit-compliance',
  vulnerabilityInsights: '/insights/vulnerability',
  cicdInsights: '/insights/ci-cd',
  productivityInsights: '/insights/productivity',
  accessInsights: '/insights/access',
  globalSearch: '/global-search',
  globalSearchInsights: '/global-search-insights',
  unauthorized: '/unauthorized',
  signout: '/signout',
  notFound: '*',
};

export const generateRoutingList = () => {
  const Dashboard = lazy(() =>
    import('pages/dashboard/dashboard')
      .then((module) => ({
        default: module.Dashboard,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const EntityGraph = lazy(() =>
    import('pages/entity-graph/entity-graph')
      .then((module) => ({
        default: module.EntityGraph,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Alerts = lazy(() =>
    import('pages/alerts/alerts')
      .then((module) => ({
        default: module.Alerts,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Identities = lazy(() =>
    import('pages/identities/identities')
      .then((module) => ({
        default: module.Identities,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const IdentityDetails = lazy(() =>
    import('pages/identities/identity-details/identity-details')
      .then((module) => ({
        default: module.IdentityDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CommitDetails = lazy(() =>
    import('pages/identities/components/flow-graph/flow-graph')
      .then((module) => ({
        default: module.FlowGraph,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Connectors = lazy(() =>
    import('pages/connectors/connectors')
      .then((module) => ({
        default: module.Connectors,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const ConnectorDetails = lazy(() =>
    import('pages/connectors/components/connector-details/connector-details')
      .then((module) => ({
        default: module.ConnectorDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );
  const Policies = lazy(() =>
    import('pages/policies/policies')
      .then((module) => ({
        default: module.Policies,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );
  const Runbooks = lazy(() =>
    import('pages/runbooks/runbooks')
      .then((module) => ({
        default: module.Runbooks,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );
  const AddRunbook = lazy(() =>
    import('pages/custom-runbooks/add-runbook')
      .then((module) => ({
        default: module.AddRunbook,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const EditPolicy = lazy(() =>
    import('pages/edit-policy/edit-policy')
      .then((module) => ({
        default: module.EditPolicy,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const PolicyCriteria = lazy(() =>
    import('pages/policy-criteria/policy-criteria')
      .then((module) => ({
        default: module.PolicyCriteria,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Assets = lazy(() =>
    import('pages/assets/assets')
      .then((module) => ({
        default: module.Assets,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Artifactory = lazy(() =>
    import('pages/artifactory/artifactory')
      .then((module) => ({
        default: module.Artifactory,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CloudAssets = lazy(() =>
    import('pages/cloud-assets/cloud-assets')
      .then((module) => ({
        default: module.CloudAssets,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CloudAlerts = lazy(() =>
    import('pages/cloud-alerts/cloud-alerts')
      .then((module) => ({
        default: module.CloudAlerts,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CloudAccounts = lazy(() =>
    import('pages/cloud-accounts/cloud-accounts')
      .then((module) => ({
        default: module.CloudAccounts,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const ContainerAlerts = lazy(() =>
    import('pages/container-alerts/container-alerts')
      .then((module) => ({
        default: module.ContainerAlerts,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Registry = lazy(() =>
    import('pages/registry/registry')
      .then((module) => ({
        default: module.Registry,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const AssetDetails = lazy(() =>
    import('pages/assets/asset-details/asset-details')
      .then((module) => ({
        default: module.AssetDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const ArtifactoryDetails = lazy(() =>
    import('pages/assets/asset-details/artifactory-details')
      .then((module) => ({
        default: module.ArtifactoryDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CloudAssetDetails = lazy(() =>
    import('pages/assets/asset-details/cloud-assets-details')
      .then((module) => ({
        default: module.CloudAssetDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );
  const CloudAccountDetails = lazy(() =>
    import('pages/assets/asset-details/cloud-account-details')
      .then((module) => ({
        default: module.CloudAccountDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Branches = lazy(() =>
    import('pages/branches/branches')
      .then((module) => ({
        default: module.Branches,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Teams = lazy(() =>
    import('pages/teams/team-tabs')
      .then((module) => ({
        default: module.TeamTabs,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const TeamsDetails = lazy(() =>
    import('pages/teams/source-code-teams/team-details/team-details')
      .then((module) => ({
        default: module.TeamDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const OrgTeamsDetails = lazy(() =>
    import('pages/teams/organizational-teams/org-team-details/org-team-details')
      .then((module) => ({
        default: module.OrgTeamDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const OpensourceDependencies = lazy(() =>
    import('pages/opensource-dependencies/opensource-dependencies')
      .then((module) => ({
        default: module.OpenSourceDependencies,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const OpensourceDependencyDetails = lazy(() =>
    import(
      'pages/opensource-dependencies/opensource-dependency-details/opensource-dependencies-details'
    )
      .then((module) => ({
        default: module.OpensourceDependencyDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Reporting = lazy(() =>
    import('pages/reporting/reporting')
      .then((module) => ({
        default: module.Reporting,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Reports = lazy(() =>
    import('pages/reports/reports-list')
      .then((module) => ({
        default: module.ReportsList,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const TeamVulnerabilityReport = lazy(() =>
    import('pages/reports/team-vulnerability-report')
      .then((module) => ({
        default: module.TeamVulnerabilityReport,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );
  const OrgVulnerabilityReport = lazy(() =>
    import('pages/org-report/org-vulnerability-report')
      .then((module) => ({
        default: module.OrgVulnerabilityReport,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const OrgRiskReport = lazy(() =>
    import('pages/org-report/org-risk-report')
      .then((module) => ({
        default: module.OrgRiskReport,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const LabeledTeamReport = lazy(() =>
    import('pages/labeled-report/labeled-report')
      .then((module) => ({
        default: module.LabeledTeamReport,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const UserManagement = lazy(() =>
    import('pages/user-management/user-management')
      .then((module) => ({
        default: module.UserManagement,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const PackageDependencyTree = lazy(() =>
    import('pages/package-dependency-tree/package-dependency-tree')
      .then((module) => ({
        default: module.PackageDependencyTree,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Secrets = lazy(() =>
    import('pages/secrets/secrets')
      .then((module) => ({
        default: module.Secrets,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const TenantSettings = lazy(() =>
    import('pages/tenant-settings/tenant-settings')
      .then((module) => ({
        default: module.TenantSettings,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const TenantSettingsWatchHide = lazy(() =>
    import('pages/tenant-settings-watch-hide/tenant-settings-watch-hide')
      .then((module) => ({
        default: module.TenantSettingsWatchHide,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const RecommendationsStatus = lazy(() =>
    import('pages/recommendations-status/recommendations-status')
      .then((module) => ({
        default: module.RecommendationsStatus,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const Recommendations = lazy(() =>
    import('pages/recommendations/recommendations').then((module) => ({
      default: module.Recommendations,
    })),
  );

  const RecommendationsIdentity = lazy(() =>
    import('pages/recommendations-identity/recommendations-identity').then(
      (module) => ({
        default: module.RecommendationsIdentity,
      }),
    ),
  );

  const RecommendationsAsset = lazy(() =>
    import('pages/recommendations-asset/recommendations-asset').then(
      (module) => ({
        default: module.RecommendationsAsset,
      }),
    ),
  );

  const RecommendationsTeam = lazy(() =>
    import('pages/recommendations-team/recommendations-team').then(
      (module) => ({
        default: module.RecommendationsTeam,
      }),
    ),
  );

  const RecommendationUserDetails = lazy(() =>
    import(
      'pages/recommendation-users/components/recommendation-user-details'
    ).then((module) => ({
      default: module.RecommendationUserDetails,
    })),
  );

  const RecommendationDetails = lazy(() =>
    import(
      'pages/recommendations-overview/components/recommendation-details'
    ).then((module) => ({
      default: module.RecommendationDetails,
    })),
  );

  const RecommendationTeamDetails = lazy(() =>
    import(
      'pages/recommendation-teams/components/recommendation-team-details'
    ).then((module) => ({
      default: module.RecommendationTeamDetails,
    })),
  );

  const RecommendationTeamMemberDetails = lazy(() =>
    import(
      'pages/recommendation-team-members/components/recommendation-details'
    ).then((module) => ({
      default: module.RecommendationTeamMemberDetails,
    })),
  );

  const ComplianceOverview = lazy(() =>
    import('pages/compliance-overview/compliance-overview')
      .then((module) => ({
        default: module.ComplianceOverview,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const ComplianceDetails = lazy(() =>
    import('pages/compliance-overview/compliance-details/compliance-details')
      .then((module) => ({
        default: module.ComplianceDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CompliancePolicyDetails = lazy(() =>
    import(
      'pages/compliance-overview/compliance-details/compliance-policy-details/compliance-policy-details'
    )
      .then((module) => ({
        default: module.CompliancePolicyDetails,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CreateCompliance = lazy(() =>
    import('pages/create-compliance/CreateCompliance')
      .then((module) => ({
        default: module.CreateCompliance,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const VulnerabilityInsights = lazy(() =>
    import('pages/vulnerability-insights/vulnerability-insights')
      .then((module) => ({
        default: module.VulnerabilityInsights,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const CICDInsights = lazy(() =>
    import('pages/ci-cd-insights/ci-cd-insights')
      .then((module) => ({
        default: module.CICDInsights,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const ProductivityInsights = lazy(() =>
    import('pages/productivity-insights/productivity-insights')
      .then((module) => ({
        default: module.ProductivityInsights,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const AccessInsights = lazy(() =>
    import('pages/access-insights/access-insights')
      .then((module) => ({
        default: module.AccessInsights,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  const GlobalSearch = lazy(() =>
    import('pages/global-search/global-search')
      .then((module) => ({
        default: module.GlobalSearch,
      }))
      .catch(() => ({
        default: ErrorPage,
      })),
  );

  return [
    {
      id: 'dashboard',
      path: paths.dashboard,
      element: <Dashboard />,
      isRender: true,
    },
    {
      id: 'entity-graph',
      path: paths.entityGraph,
      element: <EntityGraph />,
      isRender: true,
    },
    {
      id: 'entity-graph',
      path: paths.entityGraph,
      element: <EntityGraph />,
      isRender: true,
    },
    {
      id: 'package-dependency-tree',
      path: paths.packageDependencyTreeById,
      element: <PackageDependencyTree />,
      isRender: true,
    },
    {
      id: 'alerts',
      path: paths.alerts,
      element: <Alerts />,
      isRender: true,
    },
    {
      id: 'connectors',
      path: paths.connectors,
      element: <Connectors />,
      isRender: true,
    },
    {
      id: 'connectorDetails',
      path: paths.connectorsDetails,
      element: <ConnectorDetails />,
      isRender: true,
    },
    {
      id: 'policies',
      path: paths.policies,
      element: <Policies />,
      isRender: true,
    },
    {
      id: 'runbooks',
      path: paths.runbooks,
      element: <Runbooks />,
      isRender: true,
    },
    {
      id: 'addRunbook',
      path: paths.addRunbook,
      element: <AddRunbook />,
      isRender: true,
    },
    {
      id: 'addRunbook',
      path: paths.editRunbook,
      element: <AddRunbook />,
      isRender: true,
    },
    {
      id: 'edit-policy',
      path: paths.editPolicy,
      element: <EditPolicy />,
      isRender: true,
    },
    {
      id: 'policy-criteria',
      path: paths.policyCriteria,
      element: <PolicyCriteria />,
      isRender: true,
    },
    {
      id: 'identities',
      path: paths.identities,
      element: <Identities />,
      isRender: true,
    },
    {
      id: 'identityDetails',
      path: paths.identityDetails,
      element: <IdentityDetails />,
      isRender: true,
    },
    {
      id: 'commitDetails',
      path: paths.commitDetails,
      element: <CommitDetails />,
      isRender: true,
    },
    {
      id: 'assets',
      path: paths.assets,
      element: <Assets />,
      isRender: true,
    },
    {
      id: 'assetDetails',
      path: paths.assetsDetails,
      element: <AssetDetails />,
      isRender: true,
    },
    {
      id: 'artifactoryDetails',
      path: paths.artifactoryDetails,
      element: <ArtifactoryDetails />,
      isRender: true,
    },
    {
      id: 'cloudAssetDetails',
      path: paths.cloudAssetsDetails,
      element: <CloudAssetDetails />,
      isRender: true,
    },
    {
      id: 'cloudAccountDetails',
      path: paths.cloudAccountDetails,
      element: <CloudAccountDetails />,
      isRender: true,
    },
    {
      id: 'branches',
      path: paths.branches,
      element: <Branches />,
      isRender: true,
    },
    {
      id: 'teams',
      path: paths.teams,
      element: <Teams />,
      isRender: true,
    },
    {
      id: 'artifactory',
      path: paths.artifactory,
      element: <Artifactory />,
      isRender: true,
    },
    {
      id: 'cloudAssets',
      path: paths.cloudAssets,
      element: <CloudAssets />,
      isRender: true,
    },
    {
      id: 'cloudAccounts',
      path: paths.cloudAccounts,
      element: <CloudAccounts />,
      isRender: true,
    },
    {
      id: 'cloudAlerts',
      path: paths.cloudAlerts,
      element: <CloudAlerts />,
      isRender: true,
    },
    {
      id: 'containerAlerts',
      path: paths.containerAlerts,
      element: <ContainerAlerts />,
      isRender: true,
    },
    {
      id: 'registry',
      path: paths.registry,
      element: <Registry />,
      isRender: true,
    },
    {
      id: 'console',
      path: paths.console,
      element: <ConsoleAudit />,
      isRender: true,
    },
    {
      id: 'teamsDetails',
      path: paths.teamsDetails,
      element: <TeamsDetails />,
      isRender: true,
    },
    {
      id: 'orgTeamsDetails',
      path: paths.orgTeamsDetails,
      element: <OrgTeamsDetails />,
      isRender: true,
    },
    {
      id: 'opensource-dependencies',
      path: paths.opensourceDependencies,
      element: <OpensourceDependencies />,
      isRender: true,
    },
    {
      id: 'opensource-dependency-details',
      path: paths.opensourceDependencyDetails,
      element: <OpensourceDependencyDetails />,
      isRender: true,
    },
    {
      id: 'secrets',
      path: paths.secrets,
      element: <Secrets />,
      isRender: true,
    },
    {
      id: 'sast',
      path: paths.sast,
      element: <SastScan />,
      isRender: true,
    },
    {
      id: 'dast',
      path: paths.dast,
      element: <DastScan />,
      isRender: true,
    },
    {
      id: 'iac',
      path: paths.iac,
      element: <IacScan />,
      isRender: true,
    },
    {
      id: 'trivy',
      path: paths.trivy,
      element: <TrivyScan />,
      isRender: true,
    },
    {
      id: 'trivyDetails',
      path: paths.trivyDetails,
      element: <TrivyScanDetails />,
      isRender: true,
    },
    {
      id: 'pr-secrets',
      path: paths.prSecrets,
      element: <PRSecrets />,
      isRender: true,
    },
    {
      id: 'pr-secrets-details',
      path: paths.prSecretsDetails,
      element: <PRSecretsDetails />,
      isRender: true,
    },
    {
      id: 'vulnerability-insights',
      path: paths.vulnerabilityInsights,
      element: <VulnerabilityInsights />,
      isRender: true,
    },
    {
      id: 'ci-cd-insights',
      path: paths.cicdInsights,
      element: <CICDInsights />,
      isRender: true,
    },
    {
      id: 'productivity-insights',
      path: paths.productivityInsights,
      element: <ProductivityInsights />,
      isRender: true,
    },
    {
      id: 'access-insights',
      path: paths.accessInsights,
      element: <AccessInsights />,
      isRender: true,
    },
    {
      id: 'globalSearch',
      path: paths.globalSearch,
      element: <GlobalSearch />,
      isRender: true,
    },
    {
      id: 'globalSearchInsights',
      path: paths.globalSearchInsights,
      element: <GlobalSearchOpenAI />,
      isRender: true,
    },
    {
      id: 'reporting',
      path: paths.reporting,
      element: <Reporting />,
      isRender: true,
    },
    {
      id: 'reports',
      path: paths.reports,
      element: <Reports />,
      isRender: true,
    },
    {
      id: 'team-vulnerability-report',
      path: paths.teamVulnerabilityReport,
      element: <TeamVulnerabilityReport />,
      isRender: true,
    },
    {
      id: 'org-vulnerability-report',
      path: paths.orgVulnerabilityReport,
      element: <OrgVulnerabilityReport />,
      isRender: true,
    },
    {
      id: 'org-risk-report',
      path: paths.orgRiskReport,
      element: <OrgRiskReport />,
      isRender: true,
    },
    {
      id: 'labeled-team-report',
      path: paths.labeledTeamReport,
      element: <LabeledTeamReport />,
      isRender: true,
    },
    {
      id: 'userManagement',
      path: paths.userManagement,
      element: <UserManagement />,
      isRender: true,
    },
    {
      id: 'tenantSettings',
      path: paths.tenantSettings,
      element: <TenantSettings />,
      isRender: true,
    },
    {
      id: 'tenantSettingsWatchHide',
      path: paths.tenantSettingsWatchHide,
      element: <TenantSettingsWatchHide />,
      isRender: true,
    },
    {
      id: 'recommendationsStatus',
      path: paths.recommendationsStatus,
      element: <RecommendationsStatus />,
      isRender: true,
    },
    {
      id: 'recommendations-overview',
      path: paths.recommendationsOverview,
      element: <Recommendations />,
      isRender: true,
    },
    {
      id: 'recommendations-overview-identity',
      path: paths.recommendationsOverviewIdentity,
      element: <RecommendationsIdentity />,
      isRender: true,
    },
    {
      id: 'recommendations-overview-asset',
      path: paths.recommendationsOverviewAsset,
      element: <RecommendationsAsset />,
      isRender: true,
    },
    {
      id: 'recommendations-overview-team',
      path: paths.recommendationsOverviewTeam,
      element: <RecommendationsTeam />,
      isRender: true,
    },
    {
      id: 'recommendation-details',
      path: paths.recommendationDetails,
      element: <RecommendationDetails />,
      isRender: true,
    },
    {
      id: 'recommendation-user-details',
      path: paths.recommendationUserDetails,
      element: <RecommendationUserDetails />,
      isRender: true,
    },
    {
      id: 'recommendation-team-details',
      path: paths.recommendationTeamDetails,
      element: <RecommendationTeamDetails />,
      isRender: true,
    },
    {
      id: 'recommendation-team-member-details',
      path: paths.recommendationTeamMemberDetails,
      element: <RecommendationTeamMemberDetails />,
      isRender: true,
    },
    {
      id: 'compliance-overview',
      path: paths.complianceOverview,
      element: <ComplianceOverview />,
      isRender: true,
    },
    {
      id: 'compliance-details',
      path: paths.complianceDetails,
      element: <ComplianceDetails />,
      isRender: true,
    },
    {
      id: 'compliance-policy-details',
      path: paths.compliancePolicyDetails,
      element: <CompliancePolicyDetails />,
      isRender: true,
    },
    {
      id: 'create-compliance',
      path: paths.createCompliance,
      element: <CreateCompliance />,
      isRender: true,
    },
    {
      id: 'edit-compliance',
      path: paths.editCompliance,
      element: <CreateCompliance />,
      isRender: true,
    },
    // {
    //   id: 'jit',
    //   path: paths.jit,
    //   element: <Jit />,
    //   isRender: true,
    // },
    {
      id: 'unauthorized',
      path: paths.unauthorized,
      element: <Unauthorized />,
      isRender: true,
    },
    {
      id: 'not-found',
      path: paths.notFound,
      element: <NotFound />,
      isRender: true,
    },
  ];
};
